/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { bool, object } from 'prop-types';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { isExperienceEnabled } from '../../ConversationalUI/chatExperiences';
import { openChat } from '../../ConversationalUI/openChat';

// CMS href identifies the action independently of the customer-facing title.
export const GEMINI_SHOPPING_AGENT_LINK = '#gemini-shopping-agent';

const useStyles = makeStyles((theme) => ({
    '@keyframes ringShift': {
        from: { backgroundPosition: '0% 0' },
        to: { backgroundPosition: '200% 0' },
    },
    root: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        minHeight: '44px',
        padding: '12px',
        border: 'none',
        borderRadius: '100px',
        background: 'transparent',
        color: theme.palette.cms?.primary || theme.palette.primary.main,
        font: 'inherit',
        fontSize: '12px',
        lineHeight: '24px',
        letterSpacing: '.15px',
        fontWeight: 400,
        cursor: 'pointer',
        position: 'relative',
        whiteSpace: 'nowrap',
        '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: '3px',
        },
        '&:before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: '2px',
            borderRadius: 'inherit',
            backgroundImage: ({ gradient }) => `${gradient}` || 'linear-gradient(90deg, #3f3f3f, #717171, #b6b8b8, #717171, #3f3f3f)',
            backgroundSize: '200% 100%',
            backgroundRepeat: 'repeat-x',
            '-webkit-mask': 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            '-webkit-mask-composite': 'xor',
            mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            maskComposite: 'exclude',
            pointerEvents: 'none',
        },
        '&:hover:before, &:focus-visible:before': {
            animation: '$ringShift 2s linear infinite',
            border: '3px',
        },
        '@media (prefers-reduced-motion: reduce)': {
            '&:hover:before, &:focus-visible:before': {
                animation: 'none',
            },
        },
    },
    mobile: {
        gridArea: '1/4',
        width: '24px',
        height: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
        minWidth: '44px',
        padding: '0',
        border: 0,
        borderRadius: 0,
        '&:before': {
            display: 'none',
        },
        '& > $icon': {
            width: '24px',
            height: '24px',
        },
    },
    icon: {
        display: 'block',
        width: '12px',
        height: '12px',
        objectFit: 'contain',
        flexShrink: 0,
        marginBottom: '0 !important',
    },
}));

const GeminiShoppingAgentButton = ({ linkObj, isFromMobile }) => {
    const classes = useStyles({ gradient: linkObj?.font_color });
    const featureFlags = useSelector(getFeatureFlags);
    const title = linkObj?.link?.title;
    // Match OfferInboxLink's CMS icon shapes for desktop and mobile headers.
    const imgUrl = isFromMobile ? linkObj?.icon?.url : linkObj?.icon;

    // TODO: Add experiment eligibility when the Gemini experiment is defined.
    // See ./OfferInboxLink.js: useSelector(getOfferInboxData)?.abTestVariant and
    // shouldRenderOfferInbox gate rendering. Keep Gemini's assignment separate;
    // do not use the Offer Inbox experiment to determine shopping-agent access.
    if (!imgUrl || !title || !isExperienceEnabled('gemini', featureFlags || {})) return null;

    return (
        <button
            type="button"
            className={`sales-intent-entry-point ${classes.root} ${isFromMobile ? classes.mobile : ''}`}
            aria-label={title}
            onClick={() => openChat({ experience: 'gemini', source: 'navigation' })}
        >
            <img className={classes.icon} src={imgUrl} alt="" aria-hidden="true" />
            {!isFromMobile && <span>{title}</span>}
        </button>
    );
};

GeminiShoppingAgentButton.propTypes = {
    linkObj: object.isRequired,
    isFromMobile: bool,
};

GeminiShoppingAgentButton.defaultProps = {
    isFromMobile: false,
};

export default GeminiShoppingAgentButton;
